import { Fragment } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './Absence.module.css';

/**
 * L'assenza di dato come componente.
 * ---------------------------------------------------------------------------
 * Vincolo 4 della SPECIFICA: "Niente dati inventati. Campo vuoto ->
 * 'non disponibile'. L'assenza è uno stato di prima classe dell'interfaccia
 * e del modello."
 *
 * La copertura reale lo rende il caso NORMALE, non l'eccezione:
 * identità narrativa 68/257, POI 177/257, ~99% dei campi di servizio vuoti su
 * 256 comuni. Una pagina comune media è fatta in gran parte di questi
 * componenti: vanno progettati bene, non trattati come fallback.
 */

export interface NotAvailableProps {
  /** Sovrascrive il testo. Normalmente si lascia la chiave i18n. */
  readonly children?: ReactNode;
}

/**
 * Valore singolo non disponibile.
 * `aria-label` esplicito: uno screen reader legge "non disponibile", non un
 * trattino o uno spazio.
 */
export function NotAvailable({ children }: NotAvailableProps) {
  const { t } = useTranslation();
  const text = children ?? t('value_not_available', 'Non disponibile');
  return <span className={styles.value}>{text}</span>;
}

export interface FieldValueProps {
  /** Valore dal backend. `null`, `undefined` e stringa vuota sono tutti assenza. */
  readonly value: string | number | null | undefined;
  /** Rende il valore quando c'è (es. come `tel:` o `mailto:`). */
  readonly render?: (value: string) => ReactNode;
}

/**
 * Rende un valore o "non disponibile". È il punto UNICO dove si decide cosa
 * conta come assenza, così la regola non si sparpaglia in 40 componenti.
 */
export function FieldValue({ value, render }: FieldValueProps) {
  const isAbsent =
    value === null ||
    value === undefined ||
    (typeof value === 'string' && value.trim() === '') ||
    // Il dataset usa '-' come segnaposto in alcuni indirizzi di POI.
    (typeof value === 'string' && value.trim() === '-');

  if (isAbsent) return <NotAvailable />;
  const asString = String(value);
  return <>{render ? render(asString) : asString}</>;
}

export interface EmptySectionProps {
  readonly title: string;
  readonly children?: ReactNode;
}

/**
 * Sezione (pannello di tab) senza alcun elemento.
 * Non è un errore: la fonte non ha dati per quel comune. Il testo lo dice.
 */
export function EmptySection({ title, children }: EmptySectionProps) {
  const { t } = useTranslation();
  return (
    <div className={styles.section}>
      <p className={styles.sectionTitle}>{title}</p>
      <p className={styles.sectionBody}>
        {children ??
          t(
            'list_empty',
            'La fonte istituzionale non fornisce dati per questa sezione. Non sono stati inseriti valori di riempimento.',
          )}
      </p>
    </div>
  );
}

export interface CoverageNoteProps {
  readonly children: ReactNode;
}

/** Nota di copertura dati. Spiega che il buco è nella fonte, non nel portale. */
export function CoverageNote({ children }: CoverageNoteProps) {
  return (
    <p className={styles.coverage}>
      <span className={styles.coverageIcon} aria-hidden="true">
        ⓘ
      </span>
      <span>{children}</span>
    </p>
  );
}

export interface DefinitionListProps {
  readonly items: readonly {
    readonly term: string;
    readonly value: string | number | null | undefined;
    readonly render?: (value: string) => ReactNode;
  }[];
  readonly className?: string;
}

/**
 * Elenco etichetta/valore come `<dl>`.
 * Coppie di dati sono una definition list, non due colonne di div: la
 * relazione fra etichetta e valore deve esistere nell'albero di accessibilità
 * (WCAG 1.3.1).
 *
 * Le righe con valore assente vengono MOSTRATE con "non disponibile", non
 * omesse: nascondere il campo farebbe credere che non esista.
 */
export function DefinitionList({ items, className }: DefinitionListProps) {
  return (
    <dl className={[styles.definitionList, className].filter(Boolean).join(' ')}>
      {/* Fragment invece di un <div> wrapper: la griglia CSS agisce diretta
          su dt/dd, senza `display:contents` e senza stili inline. */}
      {items.map((item) => (
        <Fragment key={item.term}>
          <dt className={styles.definitionTerm}>{item.term}</dt>
          <dd className={styles.definitionValue}>
            <FieldValue
              value={item.value}
              {...(item.render ? { render: item.render } : {})}
            />
          </dd>
        </Fragment>
      ))}
    </dl>
  );
}
